/**
 * A small EXIF reader that pulls out two things: when the photo was taken, and
 * roughly where.
 *
 * Orientation is deliberately not read here — `createImageBitmap` with
 * `imageOrientation: 'from-image'` already applies it, and duplicating that
 * logic is how images end up rotated twice.
 *
 * The GPS half never leaves the browser on its own. It is offered to the sender
 * as a suggestion they can accept, edit or ignore, and only what they accept is
 * uploaded — see `location` in 0004_location.sql. Reading coordinates out of a
 * file is not the same as publishing somebody's home to a television in
 * somebody else's front room.
 */

const TAG_EXIF_IFD_POINTER = 0x8769;
const TAG_GPS_IFD_POINTER = 0x8825;
const TAG_DATE_TIME_ORIGINAL = 0x9003;
const TAG_DATE_TIME = 0x0132;

const TAG_GPS_LAT_REF = 0x0001;
const TAG_GPS_LAT = 0x0002;
const TAG_GPS_LON_REF = 0x0003;
const TAG_GPS_LON = 0x0004;

const TYPE_RATIONAL = 5;

export interface ExifFacts {
  /** EXIF DateTimeOriginal as epoch ms. */
  readonly takenAt: number | null;
  /** Decimal degrees, signed. Null unless the file carried a usable pair. */
  readonly latitude: number | null;
  readonly longitude: number | null;
}

/** EXIF timestamps are `YYYY:MM:DD HH:MM:SS` with no timezone — treat as local. */
const parseExifDate = (value: string): number | null => {
  const match = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
    Number(s),
  );
  const time = date.getTime();
  if (!Number.isFinite(time)) return null;
  // Cameras with a dead clock love 1980. Anything implausible is worse than
  // nothing, because it would sort to the wrong end of the timeline.
  if (time < Date.UTC(1990, 0, 1) || time > Date.now() + 86_400_000) return null;
  return time;
};

const readAscii = (view: DataView, offset: number, length: number): string => {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    const code = view.getUint8(offset + i);
    if (code === 0) break;
    out += String.fromCharCode(code);
  }
  return out;
};

interface IfdScan {
  dateOriginal: string | null;
  fallbackDate: string | null;
  exifIfdOffset: number | null;
  gpsIfdOffset: number | null;
}

const scanIfd = (
  view: DataView,
  tiffStart: number,
  ifdOffset: number,
  little: boolean,
): IfdScan => {
  const result: IfdScan = {
    dateOriginal: null,
    fallbackDate: null,
    exifIfdOffset: null,
    gpsIfdOffset: null,
  };
  const base = tiffStart + ifdOffset;
  if (base + 2 > view.byteLength) return result;

  const count = view.getUint16(base, little);
  for (let i = 0; i < count; i += 1) {
    const entry = base + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;

    const tag = view.getUint16(entry, little);
    const length = view.getUint32(entry + 4, little);

    if (tag === TAG_EXIF_IFD_POINTER) {
      result.exifIfdOffset = view.getUint32(entry + 8, little);
      continue;
    }
    if (tag === TAG_GPS_IFD_POINTER) {
      result.gpsIfdOffset = view.getUint32(entry + 8, little);
      continue;
    }
    if (tag !== TAG_DATE_TIME_ORIGINAL && tag !== TAG_DATE_TIME) continue;

    // ASCII values longer than 4 bytes live at an offset, not inline.
    const valueOffset = length > 4 ? tiffStart + view.getUint32(entry + 8, little) : entry + 8;
    if (valueOffset + Math.min(length, 20) > view.byteLength) continue;

    const text = readAscii(view, valueOffset, Math.min(length, 20));
    if (tag === TAG_DATE_TIME_ORIGINAL) result.dateOriginal = text;
    else result.fallbackDate = text;
  }
  return result;
};

/**
 * GPS coordinates are three RATIONALs — degrees, minutes, seconds — each a pair
 * of 32-bit integers, stored at an offset because 24 bytes never fits inline.
 * The sign lives separately in a one-character ref tag ('N'/'S', 'E'/'W').
 */
const readDms = (view: DataView, offset: number, little: boolean): number | null => {
  if (offset + 24 > view.byteLength) return null;
  let degrees = 0;
  for (let part = 0; part < 3; part += 1) {
    const numerator = view.getUint32(offset + part * 8, little);
    const denominator = view.getUint32(offset + part * 8 + 4, little);
    if (denominator === 0) return null;
    degrees += numerator / denominator / 60 ** part;
  }
  return Number.isFinite(degrees) ? degrees : null;
};

interface GpsScan {
  latitude: number | null;
  longitude: number | null;
}

const scanGpsIfd = (
  view: DataView,
  tiffStart: number,
  ifdOffset: number,
  little: boolean,
): GpsScan => {
  const base = tiffStart + ifdOffset;
  if (base + 2 > view.byteLength) return { latitude: null, longitude: null };

  let latitude: number | null = null;
  let longitude: number | null = null;
  let latRef = 'N';
  let lonRef = 'E';

  const count = view.getUint16(base, little);
  for (let i = 0; i < count; i += 1) {
    const entry = base + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;

    const tag = view.getUint16(entry, little);
    const type = view.getUint16(entry + 2, little);

    if (tag === TAG_GPS_LAT_REF) latRef = readAscii(view, entry + 8, 1) || 'N';
    else if (tag === TAG_GPS_LON_REF) lonRef = readAscii(view, entry + 8, 1) || 'E';
    else if (tag === TAG_GPS_LAT && type === TYPE_RATIONAL) {
      latitude = readDms(view, tiffStart + view.getUint32(entry + 8, little), little);
    } else if (tag === TAG_GPS_LON && type === TYPE_RATIONAL) {
      longitude = readDms(view, tiffStart + view.getUint32(entry + 8, little), little);
    }
  }

  if (latitude === null || longitude === null) return { latitude: null, longitude: null };
  if (latitude > 90 || longitude > 180) return { latitude: null, longitude: null };
  // A camera with no fix writes 0/0, which is in the Atlantic and is never a
  // photograph anyone took. Cheaper to drop than to explain.
  if (latitude === 0 && longitude === 0) return { latitude: null, longitude: null };

  return {
    latitude: latRef === 'S' ? -latitude : latitude,
    longitude: lonRef === 'W' ? -longitude : longitude,
  };
};

const NOTHING: ExifFacts = { takenAt: null, latitude: null, longitude: null };

export const readExif = async (file: File): Promise<ExifFacts> => {
  // EXIF lives in the first APP1 segment; 256 KB is generous headroom.
  const head = await file.slice(0, 256 * 1024).arrayBuffer();
  const view = new DataView(head);

  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return NOTHING;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) return NOTHING;
    const marker = view.getUint8(offset + 1);
    const size = view.getUint16(offset + 2);
    if (size < 2) return NOTHING;

    if (marker === 0xe1) {
      const segment = offset + 4;
      if (readAscii(view, segment, 4) !== 'Exif') return NOTHING;

      const tiffStart = segment + 6;
      if (tiffStart + 8 > view.byteLength) return NOTHING;

      const endian = view.getUint16(tiffStart);
      if (endian !== 0x4949 && endian !== 0x4d4d) return NOTHING;
      const little = endian === 0x4949;

      const ifd0 = scanIfd(view, tiffStart, view.getUint32(tiffStart + 4, little), little);
      const exifIfd =
        ifd0.exifIfdOffset === null
          ? null
          : scanIfd(view, tiffStart, ifd0.exifIfdOffset, little);

      const date = ifd0.dateOriginal ?? exifIfd?.dateOriginal ?? ifd0.fallbackDate;
      // The pointer is nearly always in IFD0, but a handful of cameras put it
      // in the Exif IFD instead, so take whichever turned up.
      const gpsOffset = ifd0.gpsIfdOffset ?? exifIfd?.gpsIfdOffset ?? null;
      const gps =
        gpsOffset === null
          ? { latitude: null, longitude: null }
          : scanGpsIfd(view, tiffStart, gpsOffset, little);

      return { takenAt: date ? parseExifDate(date) : null, ...gps };
    }

    // 0xDA is start-of-scan: past this point it is compressed data, not metadata.
    if (marker === 0xda) return NOTHING;
    offset += 2 + size;
  }
  return NOTHING;
};

/**
 * Coordinates written the way a phone shows them. This is what goes in the
 * suggestion chip, and it is honest about being a position rather than a place:
 * naming the pub someone was standing outside needs a geocoder, and nobody is
 * served by a location field that quietly guesses wrong.
 */
export const formatCoordinates = (latitude: number, longitude: number): string => {
  const part = (value: number, positive: string, negative: string): string =>
    `${Math.abs(value).toFixed(4)}°${value >= 0 ? positive : negative}`;
  return `${part(latitude, 'N', 'S')}, ${part(longitude, 'E', 'W')}`;
};
